import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { RootState, store } from '../..';
import { Competency, CompetencySettingType, QuestionTypesWithCompetency } from '@/models/competency/competency';
import { QuestionType } from '@/models/evalSetting/question-type';
import { GrowingPurpose } from '@/models/student/growing-purpose';
import { Trend } from '@/models/trend/trend';
import { InterviewNaviCompetency } from '@/models/interview-navi/competency';
import { masterService } from '@/service/api/master.service';
import { growingPurposeService } from '@/service/api/growing-purpose.service';
import { Destination } from '@/models/jgrow/destination';
import { Language } from '@/models/language';
import { GEnum } from '@/constants/enum.defination';

export interface IMasterState {
  competencies: Competency[]; // コンピテンシー一覧
  competenciesIncludeIsValid: Competency[]; // 設問タイプに対して有効なコンピテンシー一覧
  questionTypes: QuestionType[]; // 設問タイプ一覧
  allQuestionTypes: QuestionType[]; // 全設問タイプ一覧
  questionTypesWithCompetencies: QuestionTypesWithCompetency[]; // 設問タイプ別コンピテンシー一覧
  competencySettingType: CompetencySettingType; // 設問数
  growingPurposes: GrowingPurpose[]; // 成長目的一覧
  validGrowingPurposes: GrowingPurpose[]; // 有効な成長目的一覧
  trends: Trend[]; // 傾向チェック一覧
  destinations: Destination[];
  interviewNaviCompetencies: InterviewNaviCompetency[];
}
@Module({ dynamic: true, store, name: 'masterModule', namespaced: true })
class MasterModule extends VuexModule implements IMasterState {
  competencies: Competency[] = [];
  competenciesIncludeIsValid: Competency[] = [];
  questionTypes: QuestionType[] = [];
  allQuestionTypes: QuestionType[] = [];
  questionTypesWithCompetencies: QuestionTypesWithCompetency[] = [];
  competencySettingType: CompetencySettingType = new CompetencySettingType();
  growingPurposes: GrowingPurpose[] = [];
  validGrowingPurposes: GrowingPurpose[] = [];
  trends: Trend[] = [];
  destinations: Destination[] = [];
  interviewNaviCompetencies: InterviewNaviCompetency[] = [];
  languages: Language[] = [];

  /**
   * 該当の設問タイプに紐づいたコンピテンシー一覧取得(competency情報にisValidが含まれていることに注意)
   */
  get competenciesByQuestionTypeID(): (questionTypeID: number) => Competency[] {
    return (questionTypeID: number) => this.questionTypesWithCompetencies.find((d: QuestionTypesWithCompetency) => d.questionType.questionTypeID === questionTypeID).competencies;
  }

  get questionType(): (questionTypeID: number) => QuestionType {
    return (questionTypeID: number) => {
      return this.questionTypes.find((questionType: QuestionType) => {
        return questionType.questionTypeID === questionTypeID;
      });
    };
  }

  get questionTypeByAll(): (questionTypeID: number) => QuestionType {
    return (questionTypeID: number) => {
      return this.allQuestionTypes.find((allQuestionType: QuestionType) => {
        return allQuestionType.questionTypeID === questionTypeID;
      });
    };
  }

  get competencyMap(): Map<number, Competency> {
    return this.competencies.reduce((result: Map<number, Competency>, competency: Competency) => {
      result.set(competency.competencyID, competency);
      return result;
    }, new Map<number, Competency>());
  }

  get toCompetency(): (competencyID: number) => Competency {
    return (competencyID: number) => this.competencies.find((competency: Competency) => competency.competencyID === competencyID);
  }

  get competencyIDs(): number[] {
    return this.competencies.map((c: Competency) => c.competencyID);
  }
  get competencyCognitive(): Competency[] {
    return this.competencies.filter((d: Competency) => d.isCognitive);
  }
  get competencySelf(): Competency[] {
    return this.competencies.filter((d: Competency) => d.isSelf);
  }
  get competencyGift(): Competency[] {
    return this.competencies.filter((d: Competency) => d.isGift);
  }
  get competencyCommunity(): Competency[] {
    return this.competencies.filter((d: Competency) => d.isCommunity);
  }

  get competencyCognitiveIncludeIsValid(): Competency[] {
    return this.competenciesIncludeIsValid.filter((d: Competency) => d.isCognitive);
  }
  get competencySelfIncludeIsValid(): Competency[] {
    return this.competenciesIncludeIsValid.filter((d: Competency) => d.isSelf);
  }
  get competencyGiftIncludeIsValid(): Competency[] {
    return this.competenciesIncludeIsValid.filter((d: Competency) => d.isGift);
  }
  get competencyCommunityIncludeIsValid(): Competency[] {
    return this.competenciesIncludeIsValid.filter((d: Competency) => d.isCommunity);
  }

  get trendsDispCtrlTypeLight(): Trend[] {
    // SelfLiking = 18, // 自分好感度チェック
    // ComfortLevel = 19, // 居心地度チェック
    // Toughness = 20, // ねばり強さチェック
    return this.trends.filter((t: Trend) => {
      if ([GEnum.TrendCheck.SelfLiking, GEnum.TrendCheck.ComfortLevel, GEnum.TrendCheck.Toughness].includes(t.trendID)) return t;
    });
  }
  get trendsDispCtrlTypeLightElementarySchool(): Trend[] {
    // GEnum.TrendCheck.SelfLikingP = 23    自分好感度チェック（小学生）
    // GEnum.TrendCheck.ComfortLevelP = 24  居心地度チェック（小学生）
    // GEnum.TrendCheck.ToughnessP = 25     ねばり強さチェック（小学生）
    // GEnum.TrendCheck.SelfLikingPLower = 30    自分好感度チェック（低学年）
    // GEnum.TrendCheck.ComfortLevelPLower = 31  居心地度チェック（低学年）
    // GEnum.TrendCheck.ToughnessPLower = 32     ねばり強さチェック（低学年）
    return this.trends.filter((t: Trend) => {
      if ([GEnum.TrendCheck.SelfLikingP, GEnum.TrendCheck.ComfortLevelP, GEnum.TrendCheck.ToughnessP, GEnum.TrendCheck.SelfLikingPLower, GEnum.TrendCheck.ComfortLevelPLower, GEnum.TrendCheck.ToughnessPLower].includes(t.trendID)) return t;
    });
  }

  @Mutation
  setCompetencies(value: Competency[]) {
    this.competencies = value;
  }

  @Mutation
  setCompetenciesIncludeIsValid(value: Competency[]) {
    this.competenciesIncludeIsValid = value;
  }

  @Mutation
  setQuestionTypes(value: QuestionType[]) {
    this.questionTypes = value;
  }

  @Mutation
  setAllQuestionTypes(value: QuestionType[]) {
    this.allQuestionTypes = value;
  }

  @Mutation
  setQuestionTypesWithCompetencies(value: QuestionTypesWithCompetency[]) {
    this.questionTypesWithCompetencies = value;
  }
  @Mutation
  setCompetencySettingType(value: CompetencySettingType) {
    this.competencySettingType = value;
  }

  @Mutation
  setGrowingPurposes(value: GrowingPurpose[]) {
    this.growingPurposes = value;
  }

  @Mutation
  setValidGrowingPurposes(value: GrowingPurpose[]) {
    this.validGrowingPurposes = value;
  }

  @Mutation
  setTrends(value: Trend[]) {
    this.trends = value;
  }

  @Mutation
  setDestinations(value: Destination[]) {
    this.destinations = value;
  }

  @Mutation
  setInterviewNaviCompetencies(values: InterviewNaviCompetency[]) {
    this.interviewNaviCompetencies = values;
  }

  @Mutation
  setLanguages(value: Language[]) {
    this.languages = value;
  }

  /**
   * コンピテンシー一覧取得
   */
  @Action({ rawError: true })
  async fetchCompetency(): Promise<Competency[]> {
    const competencies: Competency[] = await masterService.getCompetencies();
    this.setCompetencies(competencies);
    return competencies;
  }

  /**
   * 設問タイプ別コンピテンシー一覧取得(competency情報にisValidフラグが含まれていることに注意)
   */
  @Action({ rawError: true })
  async fetchQuestionTypesWithCompetencies() {
    const questionTypesWithCompetencies: QuestionTypesWithCompetency[] = await masterService.getQuestionTypesWithCompetencies();
    this.setQuestionTypesWithCompetencies(questionTypesWithCompetencies);
  }

  /**
   * 設問タイプ一覧取得
   */
  @Action({ rawError: true })
  async fetchQuestionType() {
    const questionTypes: QuestionType[] = await masterService.getQuestionTypes();
    this.setQuestionTypes(questionTypes);
  }

  /**
   * 全設問タイプ一覧取得
   */
  @Action({ rawError: true })
  async fetchAllQuestionType() {
    const allQuestionTypes: QuestionType[] = await masterService.getAllQuestionTypes();
    this.setAllQuestionTypes(allQuestionTypes);
  }

  /**
   * 設問パターン(ミニ、おすすめ)に該当するコンピテンシー情報を取得
   */
  @Action({ rawError: true })
  async fetchCompetencySettingType() {
    const competencySettingType: CompetencySettingType = await masterService.getCompetencySettingTypes();
    this.setCompetencySettingType(competencySettingType);
  }

  /**
   * 成長目的一覧取得
   */
  @Action({ rawError: true })
  async fetchGrowingPurposes() {
    const growingPurposes = await growingPurposeService.getGrowingPurposesList();
    this.setGrowingPurposes(growingPurposes);
  }

  /**
   * 有効成長目的一覧取得
   * @param isValid 有効フラグ
   */
  @Action({ rawError: true })
  async fetchValidGrowingPurposes(isValid: number) {
    const validGrowingPurposes: GrowingPurpose[] = await growingPurposeService.getValidGrowingPurposesList(isValid);
    this.setValidGrowingPurposes(validGrowingPurposes);
  }

  /**
   * 傾向チェック項目一覧取得
   */
  @Action({ rawError: true })
  async fetchTrends() {
    const result = await masterService.getTrends();
    this.setTrends(result);
  }

  /**
   * JGROW イベント用 行き先一覧取得
   */
  @Action({ rawError: true })
  async fetchDestinations() {
    const result = await masterService.getDestinations();
    this.setDestinations(result);
  }

  /**
   * 生徒カルテ「面談ナビ」でのコンピテンシーごとのコメント取得
   */
  @Action({ rawError: true })
  async fetchInterviewNaviCompetencies() {
    const result = await masterService.getInterviewNaviCompetencies();
    this.setInterviewNaviCompetencies(result);
  }

  @Action({ rawError: true })
  async fetchLanguages() {
    const result = await masterService.fetchLanguages();
    this.setLanguages(result);
  }
}

export const masterModule = getModule(MasterModule);
